import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { attachHeyNowIosLauncherTouchDrag } from './heynow-chat-ios-launcher-drag.util';
import { attachHeyNowDragPositionHandlers } from './heynow-chat-position.util';
import { attachHeyNowIosNativeHandlers } from './heynow-chat-ios-native.util';
import { applyHeyNowVisibilityForUrl } from './heynow-chat-route-visibility';
import { buildHeyNowWidgetOptions } from './heynow-chat-widget-options';

const SCRIPT_ATTR = 'data-bqm-heynow-loader';

@Injectable({ providedIn: 'root' })
export class HeyNowChatLoaderService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private loadPromise: Promise<void> | null = null;
  private domHandlersTeardown: (() => void) | null = null;

  /** Single Router subscription; torn down when this service’s injector is destroyed (app teardown, TestBed destroy). */
  private readonly routeVisibilitySubscription: Subscription;

  constructor() {
    applyHeyNowVisibilityForUrl(this.router.url);
    this.routeVisibilitySubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => applyHeyNowVisibilityForUrl(this.router.url));

    this.destroyRef.onDestroy(() => {
      this.routeVisibilitySubscription.unsubscribe();
      this.domHandlersTeardown?.();
      this.domHandlersTeardown = null;
    });
  }

  loadAndInit(): Promise<void> {
    const cfg = environment.heynow;
    if (!cfg?.enabled) {
      return Promise.resolve();
    }

    if (document.getElementById('webChatHey') !== null) {
      this.attachHeyNowDomHandlersWhenReady();
      return Promise.resolve();
    }

    if (this.loadPromise !== null) {
      return this.loadPromise;
    }

    const origin = `${globalThis.location.protocol}//${globalThis.location.host}`;
    const options = buildHeyNowWidgetOptions(origin, cfg.platformId);

    this.loadPromise = new Promise((resolve, reject) => {
      const finish = () => {
        try {
          if (document.getElementById('webChatHey')) {
            this.attachHeyNowDomHandlersWhenReady();
            resolve();
            return;
          }
          if (typeof globalThis.chatHeyNow !== 'function') {
            reject(new Error('chatHeyNow is not defined'));
            return;
          }
          globalThis.chatHeyNow(options);
          this.attachHeyNowDomHandlersWhenReady();
          resolve();
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      };

      if (typeof globalThis.chatHeyNow === 'function') {
        finish();
        return;
      }

      const existing = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_ATTR}]`);
      if (existing) {
        if (existing.dataset['loaded'] === '1') {
          finish();
          return;
        }
        existing.addEventListener('load', () => {
          existing.dataset['loaded'] = '1';
          finish();
        });
        existing.addEventListener('error', () => reject(new Error('HeyNow script load failed')));
        return;
      }

      const script = document.createElement('script');
      script.src = cfg.scriptUrl;
      script.async = true;
      script.setAttribute(SCRIPT_ATTR, '1');
      script.onload = () => {
        script.dataset['loaded'] = '1';
        finish();
      };
      script.onerror = () => reject(new Error('HeyNow script load failed'));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /** Polls briefly until #webChatHey exists, then wires drag position + iOS native handlers. */
  private attachHeyNowDomHandlersWhenReady(attempt = 0): void {
    const chatRoot = document.getElementById('webChatHey');
    if (chatRoot) {
      this.domHandlersTeardown?.();
      const teardowns = [
        attachHeyNowDragPositionHandlers(chatRoot),
        attachHeyNowIosLauncherTouchDrag(chatRoot),
        attachHeyNowIosNativeHandlers(chatRoot),
      ];
      this.domHandlersTeardown = () => teardowns.forEach((teardown) => teardown());
      return;
    }
    if (attempt >= 20) {
      return;
    }
    globalThis.requestAnimationFrame(() => this.attachHeyNowDomHandlersWhenReady(attempt + 1));
  }
}
